// src/pages/CreateBlog.jsx
import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Navbar from '../components/forum/Navbar';
import { useBadge } from '../contexts/BadgeContext';
import Cropper from 'react-easy-crop';
import getCroppedImg from '../utils/cropImage';
import RichTextEditor from '../components/blog/RichTextEditor';

const CreateBlog = () => {
  const { user, getAuthenticatedApi } = useAuth();
  const { checkForNewBadges } = useBadge();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isCropping, setIsCropping] = useState(false);
  const [croppedImageFile, setCroppedImageFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/login', { state: { from: '/blog/create' } });
    }
  }, [user, navigate]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, image: 'Image size should be less than 5MB' }));
      return;
    }

    if (!file.type.startsWith('image/')) {
      setErrors((prev) => ({ ...prev, image: 'Please upload an image file' }));
      return;
    }

    setImage(file);
    setErrors((prev) => ({ ...prev, image: null }));
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
      setIsCropping(true);
    };
    reader.readAsDataURL(file);
  };

  const onCropComplete = (_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  };

  const handleCropConfirm = async () => {
    try {
      const blob = await getCroppedImg(imagePreview, croppedAreaPixels);
      const file = new File([blob], image.name, { type: image.type });
      setCroppedImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      setIsCropping(false);
    } catch (e) {
      console.error(e);
    }
  };

  const handleTagAdd = (e) => {
    e.preventDefault();
    const trimmed = tagInput.trim().toLowerCase();
    if (!trimmed) return;
    if (tags.includes(trimmed)) return;
    if (tags.length >= 10) return;
    setTags([...tags, trimmed]);
    setTagInput('');
  };

  const handleTagRemove = (t) => {
    setTags(tags.filter(tag => tag !== t));
  };

  const validateForm = () => {
    const errs = {};
    if (!title.trim()) errs.title = 'Title is required';

    // Check content length (strip HTML tags for character count)
    const textContent = content.replace(/<[^>]*>/g, '').trim();
    if (textContent.length < 50) errs.content = 'Content must be at least 50 characters';

    if (!croppedImageFile && !image) errs.image = 'Featured image is required';
    if (tags.length === 0) errs.tags = 'At least one tag is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);

    const formData = new FormData();
    formData.append('title', title.trim());
    formData.append('content', content.trim()); // Send HTML content as-is

    const finalImage = croppedImageFile || image;
    formData.append('cover_image', finalImage, finalImage.name);

    if (tags.length > 0) {
      formData.append('tags', JSON.stringify(tags));
    }

    try {
      const authenticatedApi = getAuthenticatedApi();
      const res = await authenticatedApi.post('/blog/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (res.status === 201 || res.status === 200) {
        checkForNewBadges();
        navigate('/blog', { state: { success: true } });
      } else {
        throw new Error('Failed to create blog post');
      }
    } catch (err) {
      console.error('Error creating blog:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to create blog. Please try again.';
      setErrors(prev => ({ ...prev, submit: errorMsg }));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#0B0C0D] text-white">
      <Navbar currentPage="blog" />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">Create a New Blog</h1>

        {isCropping && imagePreview && (
          <div className="fixed inset-0 bg-black/80 z-50 flex flex-col items-center justify-center">
            <div className="w-full max-w-lg h-[60vh] relative">
              <Cropper
                image={imagePreview}
                crop={crop}
                zoom={zoom}
                aspect={16 / 9}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                showGrid
              />
            </div>
            <div className="mt-4 flex gap-4">
              <button
                type="button"
                onClick={() => {
                  setIsCropping(false);
                  setImagePreview(null);
                  setImage(null);
                }}
                className="px-4 py-2 bg-gray-600 rounded hover:bg-gray-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCropConfirm}
                className="px-4 py-2 bg-primary rounded hover:bg-blue-600"
              >
                Crop & Use Image
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="title" className="block text-sm text-gray-400 mb-1">Title</label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Enter a catchy title"
              className={`w-full bg-gray-800 text-white px-4 py-2 rounded focus:outline-none focus:ring-2 ${errors.title ? 'border-red-500 ring-red-500' : 'focus:ring-primary'
                }`}
            />
            {errors.title && <p className="mt-1 text-sm text-red-500">{errors.title}</p>}
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Featured Image</label>
            <div
              onClick={() => fileInputRef.current.click()}
              className={`w-full border-2 border-dashed rounded p-6 text-center cursor-pointer ${errors.image ? 'border-red-500' : 'border-gray-700 hover:border-gray-500'
                }`}
            >
              {imagePreview && !isCropping ? (
                <div className="relative">
                  <img src={imagePreview} alt="Preview" className="w-full h-48 object-cover rounded" />
                  <button
                    type="button"
                    onClick={e => {
                      e.stopPropagation();
                      setImage(null);
                      setImagePreview(null);
                      setCroppedImageFile(null);
                    }}
                    className="absolute top-2 right-2 bg-red-500 rounded-full p-1 text-white flex items-center justify-center w-6 h-6 hover:bg-red-600"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <p className="text-gray-500">Click to upload an image (16:9 recommended)</p>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
              />
            </div>
            {errors.image && <p className="mt-1 text-sm text-red-500">{errors.image}</p>}
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Tags</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {tags.map((t, i) => (
                <span key={i} className="bg-gray-700 px-3 py-1 rounded-full flex items-center gap-2 text-sm">
                  {t}
                  <button
                    type="button"
                    onClick={() => handleTagRemove(t)}
                    className="hover:text-red-400"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <div className="flex">
              <input
                type="text"
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleTagAdd(e)}
                className={`flex-1 bg-gray-800 text-white px-4 py-2 rounded-l-lg focus:outline-none focus:ring-2 ${errors.tags ? 'border-red-500 focus:ring-red-500' : 'focus:ring-primary'
                  }`}
                placeholder="Add a tag and press Enter"
              />
              <button
                type="button"
                onClick={handleTagAdd}
                className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-r-lg transition-colors"
              >
                Add
              </button>
            </div>
            {errors.tags && <p className="mt-1 text-sm text-red-500">{errors.tags}</p>}
            <p className="mt-1 text-xs text-gray-500">Add up to 10 tags to categorize your blog.</p>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Content</label>
            <RichTextEditor
              value={content}
              onChange={setContent}
              placeholder="Write your blog content here... (min 50 characters)"
              error={errors.content}
            />
            {errors.content && <p className="mt-1 text-sm text-red-500">{errors.content}</p>}
          </div>

          {errors.submit && (
            <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg">
              {errors.submit}
            </div>
          )}

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/blog')}
              className="px-6 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-3 bg-primary hover:bg-blue-600 rounded-lg transition-colors disabled:bg-gray-700 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Publishing...
                </span>
              ) : ('Publish Blog')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateBlog;